/**
 * Schedule-geometry helpers for the Cron Jobs timeline view.
 *
 * The redesign visualises *when within an hour* each recurring job fires and
 * where same-queue jobs collide (Genius/Presto share one .NET host worker per
 * queue, so two jobs firing in the same minute serialise). These functions are
 * the source of truth the UI renders from, mirroring the server-authoritative
 * conflict algorithm the commit-preview endpoint will run in Phase 2.
 *
 * Only the minute field is expanded: every cron in the catalog is `* * * *`
 * for hour/day/month/dow, so a single hour [0,59] fully describes the cadence.
 * `expandCronMinutes` still rejects shapes it can't safely expand rather than
 * guessing — if richer crons enter the catalog the parser must grow with them.
 */

import type { JobLevel } from '@/config/jobs';

/**
 * Expected run duration per workload tier, in minutes. Drives the *length* of a
 * job's segment on the hour timeline: a segment is anchored at the fire minute
 * and extends rightward by this many minutes, so a heavy L2 visibly overlaps
 * the next fire — the same-worker contention the timeline exists to surface.
 * These are deliberate visual approximations, not measured runtimes.
 *  L1 ≈ seconds (a near-tick), L2 ≈ 10 min, L3 ≈ 20 min, L4 streams (fills
 *  to the end of the hour to read as continuous).
 */
export const LEVEL_DURATION_MINUTES: Record<JobLevel, number> = {
  L1: 1,
  L2: 10,
  L3: 20,
  L4: 60,
};

/** Human-readable run-duration estimate per tier (shown in the hover tooltip). */
export const LEVEL_DURATION_LABEL: Record<JobLevel, string> = {
  L1: '< 1 min',
  L2: '~10 min',
  L3: '~20 min',
  L4: 'streaming (continuous)',
};

/** Stroke thickness (px) per tier — a subtle secondary cue reinforcing length. */
export const LEVEL_STROKE_WIDTH: Record<JobLevel, number> = {
  L1: 3,
  L2: 4.5,
  L3: 6,
  L4: 7.5,
};

/** Minutes [0,59] a 5-field cron fires within one hour. Empty if unparseable. */
export function expandCronMinutes(expression: string): number[] {
  const fields = expression.trim().split(/\s+/);
  // Reject 6-field Hangfire crons (leading seconds field) rather than mis-reading
  // the seconds field as minutes — the parser isn't extended for them yet.
  if (fields.length === 6) return [];
  const field = fields[0] ?? '';
  if (!field) return [];
  const minutes = new Set<number>();
  for (const part of field.split(',')) {
    if (!addPart(part, minutes)) return [];
  }
  return [...minutes].sort((a, b) => a - b);
}

/** Returns false if the part is a shape we don't understand (caller bails out). */
function addPart(part: string, out: Set<number>): boolean {
  const [range, stepRaw, ...rest] = part.split('/');
  if (rest.length > 0) return false;

  let step = 1;
  if (stepRaw !== undefined) {
    step = Number(stepRaw);
    if (!Number.isInteger(step) || step <= 0) return false;
  }

  let lo: number;
  let hi: number;
  if (range === '*') {
    lo = 0;
    hi = 59;
  } else if (range.includes('-')) {
    const dashParts = range.split('-');
    if (dashParts.length > 2) return false; // malformed range like 5-10-20
    const [a, b] = dashParts;
    lo = Number(a);
    hi = Number(b);
  } else {
    lo = Number(range);
    // A bare `5/15` means "from 5 to the end of the field, every 15".
    hi = stepRaw !== undefined ? 59 : lo;
  }

  if (!Number.isInteger(lo) || !Number.isInteger(hi)) return false;
  if (lo < 0 || hi > 59 || lo > hi) return false;

  for (let v = lo; v <= hi; v += step) out.add(v);
  return true;
}

/**
 * Smallest gap between consecutive fires — the job's tightest cadence.
 * Drives dot colour (≤5 min = cyan "frequent", >5 min = purple). Infinity when
 * a job fires at most once per hour.
 */
export function fireStepMinutes(minutes: number[]): number {
  if (minutes.length < 2) return Infinity;
  let smallest = Infinity;
  for (let i = 1; i < minutes.length; i++) {
    smallest = Math.min(smallest, minutes[i] - minutes[i - 1]);
  }
  // Fold in the wrap-around gap so a job firing near the hour boundary (e.g.
  // :55 then :00 next hour) reads as frequent, not sparse.
  const wrap = 60 - minutes[minutes.length - 1] + minutes[0];
  return Math.min(smallest, wrap);
}

/** Clamp a minute to the valid hour range [0,59], guarding NaN/Infinity. */
export function clampMinute(minute: number): number {
  if (!Number.isFinite(minute)) return 0;
  return Math.min(59, Math.max(0, Math.round(minute)));
}

/**
 * Whether a cron's minute field is one we can re-phase by dragging: a single
 * minute (e.g. "17") or a stepped series (every-N, optionally offset, such as
 * "2-59/5" or "8-30/15"). Lists and a bare star are left to the Edit modal —
 * dragging them is ambiguous.
 */
export function isDraggableCron(expression: string): boolean {
  const field = expression.trim().split(/\s+/)[0] ?? '';
  return /^\d+$/.test(field) || /^(\*|\d+(?:-\d+)?)\/\d+$/.test(field);
}

/**
 * Re-phase a cron series by the drag delta (Phase 2, "shift series offset"):
 * grabbing any fire of an every-5 series and sliding it +2 min yields "2-59/5"
 * — the whole cadence shifts, it is not collapsed to a single minute. A
 * single-minute cron simply moves to the drop target. Returns null for shapes
 * we don't re-phase.
 */
export function shiftCronMinute(expression: string, fromMinute: number, toMinute: number): string | null {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const minuteField = fields[0];
  const rest = fields.slice(1).join(' ');
  const to = clampMinute(toMinute);

  // Single explicit minute → move it directly.
  if (/^\d+$/.test(minuteField)) {
    return `${to} ${rest}`;
  }

  // Stepped series → preserve the step, re-phase the start by the drag delta.
  const stepMatch = minuteField.match(/^(\*|\d+(?:-\d+)?)\/(\d+)$/);
  if (stepMatch) {
    const step = Number(stepMatch[2]);
    if (!Number.isInteger(step) || step <= 0) return null;
    const range = stepMatch[1];
    const oldOffset = range === '*' ? 0 : Number(range.split('-')[0]);
    if (!Number.isInteger(oldOffset)) return null;
    const delta = to - fromMinute;
    const newOffset = (((oldOffset + delta) % step) + step) % step;
    return `${newOffset}-59/${step} ${rest}`;
  }

  return null;
}

export interface ScheduledJob {
  jobId: string;
  queue: string;
  effectiveCron: string;
  enabled: boolean;
}

export interface ConflictInfo {
  /** Per job: the minutes at which it collides with another job on its queue. */
  byJob: Map<string, Set<number>>;
  /** Per queue: the distinct minutes that host a collision. */
  byQueue: Map<string, Set<number>>;
  /** Distinct (queue, minute) collision pairs across the whole catalog. */
  pairCount: number;
}

/**
 * Same-queue collision detection. Disabled jobs don't fire, so they neither
 * collide nor contribute density. Mirrors the §6.4 server algorithm: group by
 * queue, then any minute claimed by >1 job is a conflict for every job there.
 */
export function detectConflicts(jobs: ScheduledJob[]): ConflictInfo {
  const byJob = new Map<string, Set<number>>();
  const byQueue = new Map<string, Set<number>>();
  for (const job of jobs) byJob.set(job.jobId, new Set());

  const queues = new Map<string, ScheduledJob[]>();
  for (const job of jobs) {
    if (!job.enabled) continue;
    const bucket = queues.get(job.queue);
    if (bucket) bucket.push(job);
    else queues.set(job.queue, [job]);
  }

  let pairCount = 0;
  for (const [queue, queueJobs] of queues) {
    const minuteOccupants = new Map<number, string[]>();
    for (const job of queueJobs) {
      for (const minute of expandCronMinutes(job.effectiveCron)) {
        const occupants = minuteOccupants.get(minute);
        if (occupants) occupants.push(job.jobId);
        else minuteOccupants.set(minute, [job.jobId]);
      }
    }
    const queueConflictMinutes = new Set<number>();
    for (const [minute, occupants] of minuteOccupants) {
      if (occupants.length <= 1) continue;
      queueConflictMinutes.add(minute);
      pairCount += 1;
      for (const jobId of occupants) byJob.get(jobId)?.add(minute);
    }
    if (queueConflictMinutes.size > 0) byQueue.set(queue, queueConflictMinutes);
  }

  return { byJob, byQueue, pairCount };
}
